<template>
  <UApp :toaster="toaster">
    <div
      class="flex flex-col bg-[#0F172B]"
      :class="containedScroll ? 'h-screen overflow-hidden' : 'min-h-screen custom-scroll'"
    >
      <!-- 顶部 Header + Banner（整页滚动时仍然 sticky） -->
      <div class="sticky top-0 z-9999" ref="stickyWrapperRef">
        <TopBanners />
        <FotoHeader />
      </div>

      <!-- 主体区域：左侧固定导航 + 右侧页面内容（随页面滚动） -->
      <div
        class="flex flex-1"
        :class="containedScroll ? 'min-h-0 overflow-hidden' : ''"
      >
        <!-- 左侧导航：固定在左侧，自己内部滚动 -->
        <ToolAside class="fixed left-0 bottom-0 w-64" :style="toolAsideStyle" />

        <!-- 右侧主体：整体右移 64px，内部不再 overflow-y-auto -->
        <div
          class="flex-1 lg:ml-64 flex flex-col overflow-y-auto right-scroll"
          :class="containedScroll ? 'min-h-0 overflow-x-hidden' : ''"
        >
          <!-- 页面主体内容（高度由内容决定，靠浏览器默认滚动） -->
          <NuxtPage class="flex-1" />
        </div>
      </div>

      <!-- 底部区域：同样右移 64px，预留左侧导航宽度 -->
      <div class="lg:ml-64 flex flex-col">
        <!-- <IntroSection /> -->
        <FotoFooter v-if="showFooter" />
        <div v-else class="h-5"></div>
      </div>
    </div>

    <!-- 认证模态框 -->
    <SignUpModal
      :is-open="isSignUpModalOpen"
      @close="closeSignUpModal"
      @switch-to-sign-in="switchToSignIn"
    />
    <SignInModal
      :is-open="isSignInModalOpen"
      @close="closeSignInModal"
      @switch-to-sign-up="switchToSignUp"
    />

    <!-- 积分弹窗 -->
    <NotEnoughCredits
      v-model:is-open="isCreditsModalOpen"
      :required-credits="requiredCredits"
      :current-balance="currentCredits"
      :show-required-credits="showRequiredCredits"
      :view="creditsModalView"
      @close="closeCreditsModal"
    />

    <GlobalImagePreview
      :is-open="isImagePreviewOpen"
      :src="imagePreviewSrc"
      :compare="imagePreviewCompare"
      @close="closeImagePreview"
    />
  </UApp>
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  nextTick,
  watch,
} from "vue";
import { useStore } from "vuex";
import ToolAside from "@/components/ToolAsideNew.vue";
import TopBanners from "@/components/TopBanners.vue";
import defaultCssUrl from "~/assets/styles/new-index.css?url";
import FotoHeader from "@/components/FotoHeaderNew.vue";
import FotoFooter from "@/components/FotoFooterNew.vue";
import SignUpModal from "@/components/loginModal/SignUpModal.vue";
import SignInModal from "@/components/loginModal/SignInModal.vue";
import NotEnoughCredits from "@/components/newModal/NotEnoughCredits.vue";
import GlobalImagePreview from "@/components/newModal/GlobalImagePreview.vue";

const toaster = { position: "top-right" };
const route = useRoute();
const showFooter = computed(() => route.meta.showFooter ?? true);
const containedScroll = computed(() => route.meta.containedScroll ?? false);
useHead({
  link: [
    {
      rel: "stylesheet",
      href: defaultCssUrl,
      crossorigin: "anonymous",
      key: "layout-style",
    },
  ],
  htmlAttrs: { class: "dark" }, // 确保 SSR 也加上 dark
});

defineOptions({
  name: "default-new-layout",
});

const store = useStore();

const stickyWrapperRef = ref(null);
const DEFAULT_TOOL_ASIDE_TOP = 60;
const toolAsideTop = ref(DEFAULT_TOOL_ASIDE_TOP);

const toolAsideStyle = computed(() => ({
  top: `${toolAsideTop.value}px`,
}));

const updateToolAsideTop = () => {
  if (typeof window === "undefined" || !stickyWrapperRef.value) {
    toolAsideTop.value = DEFAULT_TOOL_ASIDE_TOP;
    return;
  }
  const { height } = stickyWrapperRef.value.getBoundingClientRect();
  const measured = Number.isFinite(height) ? Math.round(height) : 0;
  toolAsideTop.value = Math.max(DEFAULT_TOOL_ASIDE_TOP, measured);
};

const handleWindowResize = () => {
  updateToolAsideTop();
};

let stickyResizeObserver = null;
const bindStickyObserver = () => {
  if (
    typeof window === "undefined" ||
    typeof ResizeObserver === "undefined" ||
    !stickyWrapperRef.value ||
    stickyResizeObserver
  ) {
    return;
  }
  stickyResizeObserver = new ResizeObserver(() => {
    updateToolAsideTop();
  });
  stickyResizeObserver.observe(stickyWrapperRef.value);
};

const unbindStickyObserver = () => {
  if (stickyResizeObserver) {
    stickyResizeObserver.disconnect();
    stickyResizeObserver = null;
  }
};

onMounted(() => {
  nextTick(() => {
    updateToolAsideTop();
    bindStickyObserver();
  });
  if (typeof window !== "undefined") {
    window.addEventListener("resize", handleWindowResize);
  }
});

onBeforeUnmount(() => {
  if (typeof window !== "undefined") {
    window.removeEventListener("resize", handleWindowResize);
  }
  unbindStickyObserver();
});

// 获取用户信息和积分
const userInfo = computed(() => store.state.user.userInfo);
const currentCredits = computed(() => userInfo.value?.credits_sum || 0);

// 使用认证模态框 composable
const {
  isSignInModalOpen,
  isSignUpModalOpen,
  closeSignInModal,
  closeSignUpModal,
  switchToSignIn,
  switchToSignUp,
} = useAuthModals();

// 使用积分弹窗 composable
const {
  isCreditsModalOpen,
  requiredCredits,
  showRequiredCredits,
  creditsModalView,
  closeCreditsModal,
} = useCreditsModal();

const {
  isImagePreviewOpen,
  imagePreviewSrc,
  imagePreviewCompare,
  closeImagePreview,
} = useImagePreview();

watch(isSignInModalOpen, (newVal) => {
  console.log(newVal);
});
</script>
